package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.StaffListUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantStaffRequestModel
import kotlinx.coroutines.launch

class StaffListViewModel(
    context: AppContext
) : ChildViewModel<StaffListUIState>(context, StaffListUIState()) {

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.staff)
        loadLocations()
        loadStaff()
    }

    override fun onResume() {
        loadStaff()
    }

    private fun applyFilter(state: StaffListUIState): StaffListUIState {
        val filtered = if (state.selectedLocationId == null) {
            state.staff
        } else {
            state.staff.filter { staff ->
                staff.merchantLocationIds?.contains(state.selectedLocationId) == true
            }
        }
        return state.copy(filteredStaff = filtered)
    }

    fun loadLocations() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) return
        scope.launch {
            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantLocations(
                    GetMerchantLocationsRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
                                var locations = responseModel?.locations ?: emptyList()
                                val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
                                val staffLocationIds = if (isStaff) {
                                    Preferences.getString(key = PREFERENCES_STAFF_LOCATION_IDS, "")
                                        .split(",").mapNotNull { it.trim().toLongOrNull() }.filter { it > 0L }
                                } else emptyList()
                                if (staffLocationIds.isNotEmpty()) {
                                    locations = locations.filter { it.id in staffLocationIds }
                                }
                                updateState { applyFilter(it.copy(locations = locations)) }
                            }
                        }
                    )
            }
        }
    }

    fun loadStaff() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) return
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantStaff(
                    GetMerchantStaffRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantStaffResponseModel
                                updateState { state ->
                                    applyFilter(
                                        state.copy(
                                            isLoading = false,
                                            staff = responseModel?.staffList ?: emptyList()
                                        )
                                    )
                                }
                            }
                        },
                        baseError = {
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                            }
                        }
                    )
            }
        }
    }

    fun setSelectedLocationId(locationId: Long?) {
        updateState { applyFilter(it.copy(selectedLocationId = locationId)) }
    }

    fun navigateToAddEmployee() {
        navigate(com.example.mystybag.appCore.logic.NavigatorAddEmployee)
    }

    fun navigateToEditEmployee(staffId: Long) {
        navigate(com.example.mystybag.appCore.logic.NavigatorEditEmployee(staffId = staffId))
    }
}
